import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import "./PartnerGames.css";
import "./PartnerGamesOfficial.css";

const COOKOUT_STREAM_PATH = "/stream/cookoutspades";
const COOKOUT_FALLBACK = {
  slug: "cookout-spades",
  name: "Cookout Spades",
  shortName: "Spades",
  provider: { id: "america-cool", name: "america.cool" },
  category: "Cards & Competition",
  description: "The official Cookout Spades game hub on GAStreaming — watch, play, follow the league, and broadcast official america.cool sessions on the dedicated Cookout channel.",
  status: "active",
  featured: true,
  links: {
    play: "https://america.cool/room/cookoutspades",
    league: "https://america.cool/league/cookoutspades",
    broadcast: "https://america.cool/broadcast/cookoutspades",
    broadcastConsole: "https://america.cool/broadcast/cookoutspades/console",
  },
  presentation: {
    heroLabel: "OFFICIAL GA PARTNER GAME",
    primaryAction: "Play Spades",
    secondaryAction: "View League",
    watchAction: "Watch on GA",
  },
};

function ExternalAction({ href, className, children }) {
  if (!href) return null;
  return <a className={className} href={href} target="_blank" rel="noreferrer noopener">{children}</a>;
}

function BroadcastMark({ state = "connected" }) {
  const active = state === "live";
  return (
    <svg className={`pg-broadcast-mark ${active ? "is-live" : ""}`} viewBox="0 0 64 42" aria-hidden="true">
      <path d="M22 10c-7 4-7 18 0 22" />
      <path d="M14 4C1 13 1 29 14 38" />
      <circle cx="32" cy="21" r="4.8" />
      <path d="M42 10c7 4 7 18 0 22" />
      <path d="M50 4c13 9 13 25 0 34" />
    </svg>
  );
}

function StateBadge({ state = "connected" }) {
  const label = state === "live" ? "LIVE" : state === "ready" ? "READY" : "CONNECTED";
  const detail = state === "live"
    ? "GA confirms active media"
    : state === "ready"
    ? "Cookout channel is prepared"
    : "Official partner channel attached";
  return (
    <div className={`pg-stream-state pg-stream-state--${state}`}>
      <BroadcastMark state={state} />
      <div>
        <strong>{label}</strong>
        <span>{detail}</span>
      </div>
    </div>
  );
}

function OfficialPartnerPanel({ channel }) {
  const state = channel?.state || "connected";
  return (
    <article className="pg-connect-card">
      <StateBadge state={state} />
      <h3>Cookout is permanently connected</h3>
      <p>
        america.cool is an official GA partner. Approved Go Live sessions route automatically to the dedicated
        Cookout Spades channel. The active host handle is session metadata only — no GA account-link form is required.
      </p>
      <div className="pg-link-form__actions">
        <Link className="pg-btn pg-btn--red" to={COOKOUT_STREAM_PATH}>Open Cookout channel</Link>
      </div>
      {!channel?.playbackConfigured ? (
        <div className="pg-form-message">Playback metadata is still syncing on the GA channel.</div>
      ) : null}
    </article>
  );
}

function PartnerGameDetail({ initialGame }) {
  const params = useParams();
  const slug = String(params.slug || initialGame?.slug || "cookout-spades");
  const [game, setGame] = useState(initialGame || COOKOUT_FALLBACK);
  const [partnerStatus, setPartnerStatus] = useState({
    linked: true,
    automatic: true,
    slug: "cookoutspades",
    url: "https://gastreaming.live/stream/cookoutspades",
    state: "connected",
    playbackConfigured: false,
  });

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl(`/api/partner-games/${encodeURIComponent(slug)}`))
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!cancelled && ok && data?.game) setGame(data.game);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    async function loadStatus() {
      try {
        const response = await fetch(apiUrl("/api/integrations/america-cool/official-status"));
        const data = await response.json().catch(() => null);
        if (!cancelled && response.ok && data?.channel) setPartnerStatus(data.channel);
      } catch {}
    }
    loadStatus();
    const timer = window.setInterval(loadStatus, 10000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  const providerName = game?.provider?.name || "america.cool";

  return (
    <main className="pg-shell">
      <section className="pg-hero pg-hero--official">
        <div className="pg-hero__glow" />
        <div className="pg-hero__content">
          <div className="pg-eyebrow"><span className="pg-live-dot" />{game?.presentation?.heroLabel || "OFFICIAL GA PARTNER GAME"}</div>
          <div className="pg-title-row">
            <div>
              <h1>{game?.name || "Cookout Spades"}</h1>
              <p>{game?.description}</p>
            </div>
            <div className="pg-suit-mark" aria-hidden="true">♠</div>
          </div>
          <div className="pg-chip-row">
            <span>{game?.category || "Cards & Competition"}</span>
            <span>Gameplay by {providerName}</span>
            <span>Broadcast on GAStreaming</span>
          </div>
          <div className="pg-actions">
            <ExternalAction href={game?.links?.play} className="pg-btn pg-btn--red">♠ {game?.presentation?.primaryAction || "Play Spades"}</ExternalAction>
            <Link className="pg-btn pg-btn--dark" to={COOKOUT_STREAM_PATH}>◉ {game?.presentation?.watchAction || "Watch on GA"}</Link>
            <ExternalAction href={game?.links?.league} className="pg-btn pg-btn--ghost">≡ {game?.presentation?.secondaryAction || "View League"}</ExternalAction>
          </div>
        </div>
      </section>

      <section className="pg-official-grid">
        <OfficialPartnerPanel channel={partnerStatus} />

        <article className="pg-contract-card">
          <div className="pg-panel-kicker">OFFICIAL LIVE INTEGRATION</div>
          <h2>Play there. Broadcast here.</h2>
          <p>america.cool owns the table, league, Face Card and score-bug state. GAStreaming owns the permanent Cookout channel, audience, live state and broadcast authorization.</p>
          <div className="pg-state-track">
            {[
              ["connected", "Partner channel attached"],
              ["ready", "Channel prepared"],
              ["live", "Active media confirmed"],
            ].map(([state, label]) => <div key={state} className={`pg-state-step ${partnerStatus?.state === state ? "is-current" : ""}`}><strong>{state}</strong><span>{label}</span></div>)}
          </div>
          <div className="pg-security-note">The america.cool server authenticates the session. GA automatically routes approved hosts to Cookout Spades; no raw stream keys or GA passwords are shared.</div>
        </article>
      </section>

      <section className="pg-service-grid">
        <article className="pg-card">
          <div className="pg-card__icon">♠</div>
          <div><h3>Permanent Cookout room</h3><p>One permanent address for digital Cookout Spades play, whether an event is live or not.</p><ExternalAction href={game?.links?.play} className="pg-inline-link">Enter the room →</ExternalAction></div>
        </article>
        <article className="pg-card">
          <div className="pg-card__icon">🏆</div>
          <div><h3>Live league board</h3><p>Rank, team, win percentage and points live on the america.cool league surface.</p><ExternalAction href={game?.links?.league} className="pg-inline-link">View league →</ExternalAction></div>
        </article>
        <article className="pg-card">
          <div className="pg-card__icon">📡</div>
          <div><h3>Broadcast score bug</h3><p>The transparent america.cool overlay rides over Cookout camera footage in OBS or Streamlabs while GA distributes the live show.</p><ExternalAction href={game?.links?.broadcast} className="pg-inline-link">Open overlay →</ExternalAction></div>
        </article>
      </section>

      <section className="pg-architecture">
        <div className="pg-section-head"><div><span>PARTNER PLATFORM</span><h2>One circuit, two specialized systems.</h2></div><Link to="/games" className="pg-inline-link">GA Games →</Link></div>
        <div className="pg-flow">
          <div className="pg-flow__node"><strong>america.cool</strong><span>Play · Face Cards · league · game state · score bug</span></div>
          <div className="pg-flow__arrow">→</div>
          <div className="pg-flow__node pg-flow__node--accent"><strong>Cookout Spades</strong><span>Physical + digital competition under one brand</span></div>
          <div className="pg-flow__arrow">→</div>
          <div className="pg-flow__node"><strong>GAStreaming</strong><span>Permanent channel · live state · viewing · chat · community · broadcast authorization</span></div>
        </div>
      </section>
    </main>
  );
}

export default function PartnerGames() {
  const params = useParams();
  const [games, setGames] = useState([COOKOUT_FALLBACK]);

  useEffect(() => {
    if (params.slug) return undefined;
    let cancelled = false;
    fetch(apiUrl("/api/partner-games"))
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!cancelled && ok && Array.isArray(data?.games) && data.games.length) setGames(data.games);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [params.slug]);

  const featured = useMemo(() => games.find((game) => game?.featured) || games[0] || COOKOUT_FALLBACK, [games]);
  if (params.slug) return <PartnerGameDetail initialGame={games.find((game) => game?.slug === params.slug) || COOKOUT_FALLBACK} />;

  return (
    <main className="pg-shell">
      <section className="pg-index-hero pg-index-hero--official">
        <div><div className="pg-eyebrow"><span className="pg-live-dot" /> GA GAMES</div><h1>Play what you watch.</h1><p>GA Games connects live channels to playable partner ecosystems while each platform keeps ownership of the relationship it does best.</p></div>
        <div className="pg-index-hero__mark">GA<br />GAMES</div>
      </section>
      <section className="pg-library">
        <div className="pg-section-head"><div><span>OFFICIAL PARTNERS</span><h2>Featured games</h2></div><span className="pg-library__count">{games.length} active</span></div>
        <div className="pg-game-grid">
          {games.map((game) => <Link key={game.slug} to={`/games/${game.slug}`} className="pg-game-card"><div className="pg-game-card__art"><span className="pg-game-card__suit">♠</span><span className="pg-game-card__status">OFFICIAL</span></div><div className="pg-game-card__body"><span>{game.category || "Partner Game"}</span><h3>{game.name}</h3><p>{game.description}</p><div className="pg-game-card__footer"><strong>Open game hub</strong><span>→</span></div></div></Link>)}
        </div>
      </section>
      {featured?.slug ? <div className="pg-feature-cta"><div><span>FEATURED PARTNER GAME</span><strong>{featured.name}</strong></div><Link className="pg-btn pg-btn--red" to={`/games/${featured.slug}`}>Enter Cookout Spades</Link></div> : null}
    </main>
  );
}
